import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../components/Button';
import type { PopoverOptions } from './context';
import {
  POPOVER_MAX_HEIGHT,
  POPOVER_WIDTH,
  breakpointBucket,
  computePlacement,
  popoverSize,
} from './placement';
import type { Placement } from './placement';

export type PopoverOutcome = { kind: 'confirm' } | { kind: 'cancel' } | { kind: 'submit'; value: string };

export interface PopoverEntry {
  id: string;
  options: PopoverOptions;
  resolve: (outcome: PopoverOutcome) => void;
}

const FLIGHT_MS = 180;

type PopoverTone = 'default' | 'danger' | 'accent';

/**
 * 弹出式浮层的强调色。
 * `accent`（蓝色强调）用于「危险但高频」的指令确认，例如起飞/降落：
 * 依然要求二次确认，但不是红色告警。
 */
const TONE_CLASSES: Record<PopoverTone, { border: string; dot: string; title: string }> = {
  default: { border: 'border-line-strong', dot: '', title: 'text-fg' },
  danger: { border: 'border-critical/60', dot: 'bg-critical', title: 'text-critical' },
  accent: { border: 'border-accent/70', dot: 'bg-accent', title: 'text-accent' },
};

interface PopoverProps {
  entry: PopoverEntry;
  /** 位置缓存：同一个 placementKey 在同一个断点下复用同一侧，保证位置稳定 */
  sideCache: Map<string, { bucket: string; side: Placement['side'] }>;
  onSettled: (id: string, outcome: PopoverOutcome) => void;
}

export function Popover({ entry, sideCache, onSettled }: PopoverProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const [entered, setEntered] = useState(false);
  const [value, setValue] = useState(entry.options.input?.defaultValue ?? '');
  const [closing, setClosing] = useState(false);

  const { options } = entry;
  const kind = options.kind ?? 'confirm';
  const isPrompt = kind === 'prompt';
  const isAlert = kind === 'alert';

  const settle = useCallback(
    (outcome: PopoverOutcome) => {
      if (closing) return;
      setClosing(true);
      setEntered(false);
      window.setTimeout(() => onSettled(entry.id, outcome), FLIGHT_MS);
    },
    [closing, entry.id, onSettled],
  );

  const measureAndPlace = useCallback(() => {
    const card = cardRef.current;
    if (!card) return;
    const anchorEl = options.trigger;
    const anchorRect = anchorEl?.isConnected
      ? anchorEl.getBoundingClientRect()
      : // 触发元素已卸载（例如点完就关闭的菜单项）时，优先用打开时的矩形快照，
        // 实在没有快照才退化为视口中心，避免浮层消失得莫名其妙
        (options.anchorRect ?? { top: window.innerHeight / 2, left: window.innerWidth / 2, width: 0, height: 0 });
    // 首帧测量时卡片还带着入场动画的 scale(0.82)，getBoundingClientRect 会把尺寸算小，
    // 于是一个 340px 宽的弹窗会被当成 280px 去排位，结果顶出视口右边缘。
    // offsetWidth/offsetHeight 取的是布局尺寸，不受 transform 影响。
    const rect = card.getBoundingClientRect();
    const size = popoverSize(
      { width: card.offsetWidth, height: card.offsetHeight },
      { width: rect.width, height: rect.height },
    );
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const bucket = breakpointBucket(viewport.width);
    const cached = sideCache.get(options.placementKey);
    const preferred = cached && cached.bucket === bucket ? cached.side : undefined;
    const next = computePlacement(anchorRect, size, viewport, preferred);
    sideCache.set(options.placementKey, { bucket, side: next.side });
    setPlacement(next);
  }, [options.placementKey, options.trigger, sideCache]);

  useLayoutEffect(() => {
    measureAndPlace();
  }, [measureAndPlace]);

  // 先量出位置再播放「从按钮飞过来」的动画，避免出现一帧错位闪烁。
  useEffect(() => {
    if (!placement) return;
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [placement]);

  useEffect(() => {
    const focusTarget = isPrompt ? inputRef.current : confirmRef.current;
    focusTarget?.focus();
  }, [isPrompt, placement]);

  useEffect(() => {
    const onViewportChange = () => measureAndPlace();
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);
    return () => {
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [measureAndPlace]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (cardRef.current?.contains(target)) return;
      if (options.trigger && options.trigger.contains(target)) return;
      settle({ kind: 'cancel' });
    };
    // 捕获阶段监听，保证页面上的 stopPropagation 不影响关闭逻辑
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [options.trigger, settle]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        settle({ kind: 'cancel' });
        return;
      }
      if (event.key === 'Enter' && !isPrompt && !event.isComposing) {
        event.preventDefault();
        settle({ kind: 'confirm' });
      }
      if (event.key === 'Tab' && cardRef.current) {
        const focusables = cardRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isPrompt, settle]);

  const tone: PopoverTone = options.tone ?? 'default';
  const toneClasses = TONE_CLASSES[tone];
  const input = isPrompt ? (options.input ?? {}) : undefined;

  const style: React.CSSProperties = {
    position: 'fixed',
    top: placement?.position.top ?? -9999,
    left: placement?.position.left ?? -9999,
    width: POPOVER_WIDTH,
    maxHeight: POPOVER_MAX_HEIGHT,
    transformOrigin: placement?.origin,
    // FLIP：从触发按钮最近的点平移到最终位置，关闭时原路飞回
    transform: entered
      ? 'translate3d(0,0,0) scale(1)'
      : `translate3d(${placement?.delta.left ?? 0}px, ${placement?.delta.top ?? 0}px, 0) scale(0.82)`,
    opacity: entered ? 1 : 0,
    transition: `transform ${FLIGHT_MS}ms cubic-bezier(0.22,1,0.36,1), opacity ${FLIGHT_MS}ms ease-out`,
    // 弹出式浮层刻意不加阴影，靠边框与背景色区分层级
    visibility: placement ? 'visible' : 'hidden',
  };

  return createPortal(
    <div
      ref={cardRef}
      role="dialog"
      aria-modal="false"
      aria-label={options.title}
      style={style}
      className={[
        'z-[1200] flex flex-col overflow-hidden rounded-lg border bg-surface-1',
        toneClasses.border,
      ].join(' ')}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div className="flex flex-col gap-1 border-b border-line px-3.5 py-3">
        <div className="flex items-center gap-2">
          {toneClasses.dot ? (
            <span className={`size-2 rounded-full ${toneClasses.dot}`} aria-hidden />
          ) : null}
          <p className={`text-sm font-semibold ${toneClasses.title}`}>{options.title}</p>
        </div>
        {options.description ? (
          <div className="text-xs leading-relaxed text-fg-muted">{options.description}</div>
        ) : null}
      </div>

      {input ? (
        <div className="px-3.5 py-3">
          {input.label ? <p className="mb-1.5 text-xs font-medium text-fg-muted">{input.label}</p> : null}
          {input.multiline ? (
            <textarea
              ref={(node) => {
                inputRef.current = node;
              }}
              value={value}
              rows={3}
              maxLength={input.maxLength}
              placeholder={input.placeholder}
              onChange={(event) => setValue(event.target.value)}
              className="w-full resize-y rounded-md border border-line bg-surface-1 px-2.5 py-2 text-sm text-fg placeholder:text-fg-subtle focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40"
            />
          ) : (
            <input
              ref={(node) => {
                inputRef.current = node;
              }}
              value={value}
              maxLength={input.maxLength}
              placeholder={input.placeholder}
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  settle({ kind: 'submit', value });
                }
              }}
              className="h-9 w-full rounded-md border border-line bg-surface-1 px-2.5 text-sm text-fg placeholder:text-fg-subtle focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40"
            />
          )}
        </div>
      ) : null}

      <div className="flex items-center justify-end gap-2 bg-surface-2 px-3.5 py-2.5">
        {isAlert ? null : (
          <Button size="sm" variant="ghost" onClick={() => settle({ kind: 'cancel' })}>
            {options.cancelText ?? '取消'}
          </Button>
        )}
        <Button
          ref={confirmRef}
          size="sm"
          variant={tone === 'danger' ? 'dangerSolid' : 'primary'}
          onClick={() => settle(isPrompt ? { kind: 'submit', value } : { kind: 'confirm' })}
        >
          {options.confirmText ?? '确定'}
        </Button>
      </div>
    </div>,
    getOverlayRoot(),
  );
}

/** 所有浮层统一挂在 body 下的固定容器里，避免被祖先的 transform / overflow 裁切。 */
export function getOverlayRoot(): HTMLElement {
  const existing = document.getElementById('overlay-root');
  if (existing) return existing;
  const root = document.createElement('div');
  root.id = 'overlay-root';
  document.body.appendChild(root);
  return root;
}
